import React, { useEffect, useState, useMemo } from 'react';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import type { ISourceOptions } from '@tsparticles/engine';
import { useThemeContext } from '@radix-ui/themes';

const FALLBACK_COLOR = '#ffffff';

export const AnimatedBackground = React.memo(() => {
    const [init, setInit] = useState(false);
    const [accentColor, setAccentColor] = useState(FALLBACK_COLOR);
    const theme = useThemeContext();

    useEffect(() => {
        initParticlesEngine(async (engine) => {
            await loadSlim(engine);
        }).then(() => setInit(true));
    }, []);

    useEffect(() => {
        const themeRoot =
            document.querySelector('.radix-themes') || document.body;
        const style = getComputedStyle(themeRoot);
        const color = style.getPropertyValue('--accent-9').trim();

        if (color) {
            setAccentColor(color);
        }
    }, [theme.accentColor]);

    const options: ISourceOptions = useMemo(
        () => ({
            fullScreen: { enable: false },
            fpsLimit: 60,
            interactivity: {
                events: { onHover: { enable: true, mode: 'repulse' } },
                modes: { repulse: { distance: 120, duration: 0.4 } },
            },
            particles: {
                color: { value: accentColor },
                links: {
                    color: accentColor,
                    distance: 150,
                    enable: true,
                    opacity: 0.2,
                    width: 1,
                },
                move: {
                    enable: true,
                    speed: 0.8,
                    direction: 'none',
                    outModes: { default: 'out' },
                },
                number: { value: 100, density: { enable: true, area: 800 } },
                opacity: { value: 0.3 },
                shape: { type: 'circle' },
                size: { value: { min: 1, max: 2 } },
            },
            detectRetina: true,
        }),
        [accentColor],
    );

    if (!init) return null;

    return (
        <Particles
            id='tsparticles'
            options={options}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
            }}
        />
    );
});
